import React, { useEffect, useState } from 'react';
import { cn } from '../../utils/cn';
import { useProjectPanelStore, CreateAssetType } from '../../store/projectPanelStore';
import { useAssetPreviewStore } from '../../store/assetPreviewStore';
import { SLOT_CORE_DEST_FOLDER } from '../../constants/slot-core';
import { getInstalledVersion } from '../../services/slot-core';

export const ProjectContextMenu: React.FC = () => {
  const {
    contextMenuPosition,
    contextMenuTargetId,
    hideContextMenu,
    assets,
    folders,
    deleteAsset,
    deleteFolder,
    duplicateAsset,
    renameAsset,
    renameFolder,
    createFolder,
    createAsset,
    selectedFolderId,
    importExternalFiles,
    cutItems,
    copyItems,
    pasteItems,
    clipboard,
  } = useProjectPanelStore();
  const [showCreateSubmenu, setShowCreateSubmenu] = useState(false);

  useEffect(() => {
    if (contextMenuPosition) {
      const handleKeyDown = (e: KeyboardEvent) => { if (e.key === 'Escape') hideContextMenu(); };
      document.addEventListener('keydown', handleKeyDown);
      return () => { document.removeEventListener('keydown', handleKeyDown); };
    }
  }, [contextMenuPosition, hideContextMenu]);

  if (!contextMenuPosition || !contextMenuTargetId) return null;

  const isFolder = !!folders[contextMenuTargetId];
  const isAsset = !!assets[contextMenuTargetId];
  const targetName = isFolder ? folders[contextMenuTargetId]?.name : assets[contextMenuTargetId]?.name;
  const isSlotCoreFolder = isFolder && folders[contextMenuTargetId]?.name === SLOT_CORE_DEST_FOLDER;

  const handleOpen = async () => {
    const asset = assets[contextMenuTargetId];
    if (!asset) {
      hideContextMenu();
      return;
    }

    const { openPreview } = useAssetPreviewStore.getState();

    switch (asset.type) {
      case 'scene':
        const { useSceneStore } = await import('../../store/sceneStore');
        await useSceneStore.getState().loadScene(asset.path);
        break;
      case 'sprite':
      case 'audio-music':
      case 'audio-sfx':
      case 'prefab':
      default:
        openPreview(asset);
    }
    hideContextMenu();
  };
  const handleDuplicate = () => { if (isAsset) duplicateAsset(contextMenuTargetId); hideContextMenu(); };
  const handleDelete = () => {
    if (confirm(`Delete "${targetName}"?`)) {
      if (isFolder) deleteFolder(contextMenuTargetId);
      else if (isAsset) deleteAsset(contextMenuTargetId);
    }
    hideContextMenu();
  };
  const handleRename = () => {
    const newName = prompt('Enter new name:', targetName);
    if (newName?.trim() && newName !== targetName) {
      if (isFolder) renameFolder(contextMenuTargetId, newName.trim());
      else if (isAsset) renameAsset(contextMenuTargetId, newName.trim());
    }
    hideContextMenu();
  };
  const handleCreateFolder = () => {
    const name = prompt('Enter folder name:');
    if (name?.trim()) createFolder(isFolder ? contextMenuTargetId : selectedFolderId, name.trim());
    hideContextMenu();
  };

  const handleUpdateSlotCore = () => {
    window.dispatchEvent(new CustomEvent('slot-core:update-requested'));
    hideContextMenu();
  };

  const handleSlotCoreInfo = async () => {
    const version = await getInstalledVersion();
    if (version) {
      alert(
        `Slot Core Info\n\nVersion: ${version.sha.slice(0, 7)}\nBranch: ${version.ref}\nInstalled: ${new Date(version.installedAt).toLocaleString()}\nFiles: ${version.filesCount}`,
      );
    } else {
      alert('Slot Core is not installed.');
    }
    hideContextMenu();
  };

  const handleImport = () => {
    // Create a file input to trigger file picker
    const input = document.createElement('input');
    input.type = 'file';
    input.multiple = true;
    input.accept = '.png,.jpg,.jpeg,.gif,.webp,.mp3,.wav,.ogg,.m4a,.js,.ts,.json,.prefab,.scene';
    input.onchange = async (e) => {
      const files = (e.target as HTMLInputElement).files;
      if (files && files.length > 0) {
        const targetFolderId = contextMenuTargetId || 'root';
        await importExternalFiles(Array.from(files), targetFolderId);
      }
    };
    input.click();
    hideContextMenu();
  };

  const handleCreateAsset = (assetType: CreateAssetType, defaultName: string) => {
    const name = prompt(`Enter ${assetType.replace('-', ' ')} name:`, defaultName);
    if (name?.trim()) {
      const targetFolder = isFolder ? contextMenuTargetId : selectedFolderId;
      createAsset(targetFolder, assetType, name.trim());
    }
    hideContextMenu();
  };

  const handleCut = () => {
    if (contextMenuTargetId) {
      cutItems([contextMenuTargetId], selectedFolderId || 'root');
    }
    hideContextMenu();
  };

  const handleCopy = () => {
    if (contextMenuTargetId) {
      copyItems([contextMenuTargetId], selectedFolderId || 'root');
    }
    hideContextMenu();
  };

  const handlePaste = async () => {
    const targetFolderId = isFolder ? contextMenuTargetId : selectedFolderId || 'root';
    await pasteItems(targetFolderId);
    hideContextMenu();
  };

  // Check if paste is available (clipboard has items)
  const canPaste = clipboard.items.length > 0 && clipboard.operation !== null;

  let left = contextMenuPosition.x;
  let top = contextMenuPosition.y;
  if (left + 200 > window.innerWidth) left = contextMenuPosition.x - 200;
  if (top + 300 > window.innerHeight) top = Math.max(10, window.innerHeight - 300);

  return (
    <>
    {/* Invisible backdrop to catch outside clicks */}
    <div style={{ position: 'fixed', inset: 0, zIndex: 9998 }} onClick={hideContextMenu} onContextMenu={(e) => { e.preventDefault(); hideContextMenu(); }} />
    <div style={{ position: 'fixed', left, top, zIndex: 9999 }} className="bg-zinc-800 border border-zinc-600 rounded shadow-lg min-w-[180px] py-1">
      {isAsset && (
        <>
          <button onClick={handleOpen} className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-left text-zinc-300 hover:bg-blue-600">📂 Open</button>
          <div className="my-1 border-t border-zinc-700" />
        </>
      )}
      {isSlotCoreFolder && (
        <>
          <button onClick={handleUpdateSlotCore} className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-left text-zinc-300 hover:bg-blue-600">🔄 Update Slot Core</button>
          <button onClick={handleSlotCoreInfo} className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-left text-zinc-300 hover:bg-blue-600">ℹ️ Slot Core Info</button>
          <div className="my-1 border-t border-zinc-700" />
        </>
      )}
      {isFolder && (
        <>
          <button onClick={handleImport} className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-left text-zinc-300 hover:bg-blue-600">📥 Import...</button>
          <div
            className="relative"
            onMouseEnter={() => setShowCreateSubmenu(true)}
            onMouseLeave={() => setShowCreateSubmenu(false)}
          >
            <button className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-left text-zinc-300 hover:bg-blue-600">
              ➕ Create
              <span className="ml-auto text-zinc-500">▶</span>
            </button>
            {showCreateSubmenu && (
              <div
                className="absolute left-full top-0 bg-zinc-800 border border-zinc-600 rounded shadow-lg min-w-[160px] py-1"
                style={{ marginLeft: -1 }}
              >
                <button onClick={handleCreateFolder} className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-left text-zinc-300 hover:bg-blue-600">📁 New Folder</button>
                <div className="my-1 border-t border-zinc-700" />
                <button onClick={() => handleCreateAsset('script-js', 'NewScript.js')} className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-left text-zinc-300 hover:bg-blue-600">📄 New Script (JS)</button>
                <button onClick={() => handleCreateAsset('script-ts', 'NewScript.ts')} className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-left text-zinc-300 hover:bg-blue-600">📄 New Script (TS)</button>
                <div className="my-1 border-t border-zinc-700" />
                <button onClick={() => handleCreateAsset('scene', 'NewScene.scene')} className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-left text-zinc-300 hover:bg-blue-600">🎬 New Scene</button>
                <button onClick={() => handleCreateAsset('prefab', 'NewPrefab.prefab')} className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-left text-zinc-300 hover:bg-blue-600">📦 New Prefab</button>
              </div>
            )}
          </div>
          <div className="my-1 border-t border-zinc-700" />
        </>
      )}
      <button onClick={handleCut} className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-left text-zinc-300 hover:bg-blue-600">✂️ Cut <span className="ml-auto text-zinc-500">Ctrl+X</span></button>
      <button onClick={handleCopy} className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-left text-zinc-300 hover:bg-blue-600">📋 Copy <span className="ml-auto text-zinc-500">Ctrl+C</span></button>
      <button onClick={handlePaste} disabled={!canPaste} className={cn('w-full flex items-center gap-2 px-3 py-1.5 text-xs text-left hover:bg-blue-600', canPaste ? 'text-zinc-300' : 'text-zinc-600')}>📄 Paste <span className="ml-auto text-zinc-500">Ctrl+V</span></button>
      {isAsset && <button onClick={handleDuplicate} className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-left text-zinc-300 hover:bg-blue-600">📄 Duplicate <span className="ml-auto text-zinc-500">Ctrl+D</span></button>}
      <button onClick={handleDelete} disabled={contextMenuTargetId === 'root'} className={cn('w-full flex items-center gap-2 px-3 py-1.5 text-xs text-left hover:bg-blue-600', contextMenuTargetId === 'root' ? 'text-zinc-600' : 'text-red-400')}>🗑️ Delete <span className="ml-auto text-zinc-500">Del</span></button>
      <div className="my-1 border-t border-zinc-700" />
      <button onClick={handleRename} disabled={contextMenuTargetId === 'root'} className={cn('w-full flex items-center gap-2 px-3 py-1.5 text-xs text-left hover:bg-blue-600', contextMenuTargetId === 'root' ? 'text-zinc-600' : 'text-zinc-300')}>✏️ Rename <span className="ml-auto text-zinc-500">F2</span></button>
      {!isFolder && (
        <>
          <div className="my-1 border-t border-zinc-700" />
          <button onClick={handleCreateFolder} className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-left text-zinc-300 hover:bg-blue-600">📁 New Folder</button>
        </>
      )}
    </div>
    </>
  );
};
